import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import * as PIXI from 'pixi.js';
import { Live2DModel } from 'pixi-live2d-display';
import { Face, Vector, Utils } from 'kalidokit';

const { lerp } = Vector;
const { clamp } = Utils;

// Global MediaPipe instance to prevent multiple instances
let globalFaceMeshInstance = null;
let globalCameraInstance = null;
let globalVideoElement = null;
let globalFaceData = null;
let globalSetupPromise = null;

// This component renders a Live2D avatar preview with face tracking animation for avatar selection
const Live2DPreview = forwardRef(({ live2dPath, width = 300, height = 400 }, ref) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMediaPipeInitializing, setIsMediaPipeInitializing] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [showInstruction, setShowInstruction] = useState(true);
  const appRef = useRef(null);
  const modelRef = useRef(null);
  const isComponentMountedRef = useRef(true);

  // Auto-hide instruction message after 5 seconds
  useEffect(() => {
    if (faceDetected && !isLoading && showInstruction) {
      const timer = setTimeout(() => {
        setShowInstruction(false);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [faceDetected, isLoading, showInstruction]);

  // Expose cleanup function to parent component
  useImperativeHandle(ref, () => ({
    cleanup: () => {
      console.log('[Live2DPreview] External cleanup called');
      // Stop camera and MediaPipe instances
      if (globalCameraInstance) {
        try {
          globalCameraInstance.stop();
          globalCameraInstance = null;
        } catch (error) {
          console.warn('[Live2DPreview] Error stopping camera:', error);
        }
      }
      
      if (globalFaceMeshInstance) {
        try {
          globalFaceMeshInstance.close();
          globalFaceMeshInstance = null;
        } catch (error) {
          console.warn('[Live2DPreview] Error closing FaceMesh:', error);
        }
      }
      
      // Stop video tracks
      if (globalVideoElement && globalVideoElement.srcObject) {
        try {
          globalVideoElement.srcObject.getTracks().forEach(track => track.stop());
        } catch (error) {
          console.warn('[Live2DPreview] Error stopping video tracks:', error);
        }
      }
      
      // Remove video element
      if (globalVideoElement && document.body.contains(globalVideoElement)) {
        try {
          document.body.removeChild(globalVideoElement);
        } catch (error) {
          console.warn('[Live2DPreview] Error removing video element:', error);
        }
      }
      
      // Reset global variables
      globalVideoElement = null;
      globalFaceData = null;
      globalSetupPromise = null;
      
      console.log('[Live2DPreview] Cleanup completed');
    }
  }));

  useEffect(() => {
    let isComponentMounted = true;
    isComponentMountedRef.current = true;

    // Handle resize to ensure canvas always fills container
    const handleResize = () => {
      if (appRef.current && containerRef.current) {
        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const newWidth = rect.width;
        const newHeight = rect.height;
        
        console.log('Resizing canvas to:', { newWidth, newHeight });
        
        // Resize the PIXI renderer
        appRef.current.renderer.resize(newWidth, newHeight);
        
        // Update the canvas element styles
        const canvas = appRef.current.view;
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.display = 'block';
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';
      }
    };

    // Use ResizeObserver for more reliable container size detection
    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    // Rig face function for model animation
    const rigFace = (result, lerpAmount = 0.7) => {
      const model = modelRef.current;
      if (!model || !result) return;
      const coreModel = model.internalModel.coreModel;

      model.internalModel.eyeBlink = undefined;

      coreModel.setParameterValueById("ParamEyeBallX", lerp(result.pupil.x, coreModel.getParameterValueById("ParamEyeBallX"), lerpAmount));
      coreModel.setParameterValueById("ParamEyeBallY", lerp(result.pupil.y, coreModel.getParameterValueById("ParamEyeBallY"), lerpAmount));

      coreModel.setParameterValueById("ParamAngleX", lerp(result.head.degrees.y, coreModel.getParameterValueById("ParamAngleX"), lerpAmount));
      coreModel.setParameterValueById("ParamAngleY", lerp(result.head.degrees.x, coreModel.getParameterValueById("ParamAngleY"), lerpAmount));
      coreModel.setParameterValueById("ParamAngleZ", lerp(result.head.degrees.z, coreModel.getParameterValueById("ParamAngleZ"), lerpAmount));

      const dampener = 0.2;
      coreModel.setParameterValueById("ParamBodyAngleX", lerp(result.head.degrees.y * dampener, coreModel.getParameterValueById("ParamBodyAngleX"), lerpAmount));
      coreModel.setParameterValueById("ParamBodyAngleY", lerp(result.head.degrees.x * dampener, coreModel.getParameterValueById("ParamBodyAngleY"), lerpAmount));
      coreModel.setParameterValueById("ParamBodyAngleZ", lerp(result.head.degrees.z * dampener, coreModel.getParameterValueById("ParamBodyAngleZ"), lerpAmount));

      let stabilizedEyes = Face.stabilizeBlink(
        {
          l: lerp(result.eye.l, coreModel.getParameterValueById("ParamEyeLOpen"), 0.7),
          r: lerp(result.eye.r, coreModel.getParameterValueById("ParamEyeROpen"), 0.7),
        },
        result.head.y
      );

      coreModel.setParameterValueById("ParamEyeLOpen", stabilizedEyes.l);
      coreModel.setParameterValueById("ParamEyeROpen", stabilizedEyes.r);

      coreModel.setParameterValueById("ParamMouthOpenY", lerp(result.mouth.y, coreModel.getParameterValueById("ParamMouthOpenY"), 0.3));
      coreModel.setParameterValueById("ParamMouthForm", 0.3 + lerp(result.mouth.x, coreModel.getParameterValueById("ParamMouthForm"), 0.3));

      coreModel.update();
    };

    // Setup MediaPipe for face tracking (global instance)
    const setupMediaPipe = async () => {
      if (!window.FaceMesh) {
        console.log('MediaPipe FaceMesh not available');
        return;
      }

      // If already set up, just return
      if (globalSetupPromise) {
        return globalSetupPromise;
      }

      console.log('[Live2DPreview] Setting up MediaPipe');
      setIsMediaPipeInitializing(true);
      
      globalSetupPromise = (async () => {
        try {
          // Add delay to prevent WASM module conflicts
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          globalFaceMeshInstance = new window.FaceMesh({
            locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
          });

          globalFaceMeshInstance.setOptions({
            maxNumFaces: 1,
            refineLandmarks: true,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5,
          });

          globalFaceMeshInstance.onResults((results) => {
            if (!isComponentMountedRef.current) return;
            
            if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
              const landmarks = results.multiFaceLandmarks[0];
              globalFaceData = Face.solve(landmarks, {
                runtime: 'mediapipe',
                video: globalVideoElement,
              });
              setFaceDetected(true);
            } else {
              setFaceDetected(false);
            }
          });

          // Add another small delay before starting camera
          await new Promise(resolve => setTimeout(resolve, 200));

          globalCameraInstance = new window.Camera(globalVideoElement, {
            onFrame: async () => {
              if (globalFaceMeshInstance && globalVideoElement && isComponentMountedRef.current) {
                await globalFaceMeshInstance.send({ image: globalVideoElement });
              }
            },
            width: 640,
            height: 360
          });

          await globalCameraInstance.start();
          console.log('[Live2DPreview] MediaPipe setup complete');
        } catch (error) {
          console.error('[Live2DPreview] MediaPipe setup failed:', error);
          // Reset global instances on error
          globalFaceMeshInstance = null;
          globalCameraInstance = null;
          globalSetupPromise = null;
        } finally {
          setIsMediaPipeInitializing(false);
        }
      })();

      return globalSetupPromise;
    };

    // Load Live2D Cubism Core
    const loadLive2DCore = () => {
      return new Promise((resolve, reject) => {
        if (window.Live2DCubismCore) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://cubism.live2d.com/sdk-web/cubismcore/live2dcubismcore.min.js';
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Live2D Cubism Core'));
        document.body.appendChild(script);
      });
    };

    // Position and scale model
    const positionAndScaleModel = (model) => {
      if (!model || !containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate scale factors for both dimensions (same logic as Live2DViewer)
      const modelWidth = model.width;
      const modelHeight = model.height;
      const scaleX = containerWidth / modelWidth;
      const scaleY = containerHeight / modelHeight;
      
      // Use the smaller scale to ensure model fits completely in available space
      let scale = Math.min(scaleX, scaleY);
      
      // Apply a small padding factor (90%) to ensure some margin
      scale = scale * 0.9;
      
      // Clamp scale between reasonable bounds
      scale = Math.max(0.2, Math.min(scale, 2.0));

      model.scale.set(scale);

      // Set anchor to top center for proper head positioning (same as Live2DViewer)
      model.anchor.set(0.5, 0);
      
      // Position from top center (same as Live2DViewer)
      const posX = containerWidth / 2;
      const posY = 0; // No padding from top

      model.position.set(posX, posY);

      console.log('[Live2DPreview] Model positioned:', {
        scale,
        posX,
        posY,
        containerWidth,
        containerHeight,
        modelWidth,
        modelHeight,
        scaleCalculations: `X=${scaleX.toFixed(3)}, Y=${scaleY.toFixed(3)}`,
        finalScale: scale.toFixed(3),
        finalSize: `${(modelWidth * scale).toFixed(0)} x ${(modelHeight * scale).toFixed(0)}`
      });
    };

    // Load and position model
    const loadAndPositionModel = async () => {
      if (!live2dPath || !appRef.current) return;

      try {
        console.log('[Live2DPreview] Loading model from:', live2dPath);
        
        // Load the model with pointer interaction disabled
        const model = await Live2DModel.from(live2dPath, { autoInteract: false });
        
        if (!isComponentMounted) {
          model.destroy();
          return;
        }

        // Store model reference
        modelRef.current = model;
        
        // Add model to stage
        appRef.current.stage.addChild(model);
        
        // Position and scale the model
        positionAndScaleModel(model);
        
        // Setup MediaPipe for face tracking (if not already set up)
        await setupMediaPipe();
        
        setIsLoading(false);
        console.log('[Live2DPreview] Model loaded successfully');
      } catch (error) {
        console.error('[Live2DPreview] Failed to load model:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    const initialize = async () => {
      if (!containerRef.current) {
        console.error("Container not available");
        return;
      }

      try {
        // Load Live2D Cubism Core if not already loaded
        await loadLive2DCore();

        // Configure Live2DModel settings
        Live2DModel.registerTicker(PIXI.Ticker);

        // Create PIXI application to fill the entire preview area
        const app = new PIXI.Application({
          width: containerRef.current.clientWidth || 300,
          height: containerRef.current.clientHeight || 400,
          backgroundColor: 0x000000,
          backgroundAlpha: 0, // Fully transparent background
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          transparent: true, // Enable transparency
          clearBeforeRender: false // Don't clear the background before rendering
        });

        containerRef.current.appendChild(app.view);
        appRef.current = app;
        
        // Force canvas to fill the entire container
        app.view.style.width = '100%';
        app.view.style.height = '100%';
        app.view.style.display = 'block';
        app.view.style.position = 'absolute';
        app.view.style.top = '0';
        app.view.style.left = '0';
        
        // Initial resize
        handleResize();
        
        // Add resize listener
        window.addEventListener('resize', handleResize);
        
        // Observe container for size changes
        if (containerRef.current) {
          resizeObserver.observe(containerRef.current);
        }

        // Load Live2D model
        if (live2dPath) {
          await loadAndPositionModel();
        }

      } catch (error) {
        console.error('[Live2DPreview] Initialization failed:', error);
        setError(error.message);
        setIsLoading(false);
      }
    };

    // Animation loop for face tracking
    const animate = () => {
      if (globalFaceData && modelRef.current && isComponentMountedRef.current) {
        rigFace(globalFaceData, 0.5);
      }
    };

    // Start animation loop
    const animationId = requestAnimationFrame(function animateLoop() {
      animate();
      requestAnimationFrame(animateLoop);
    });

    initialize();

    return () => {
      isComponentMounted = false;
      isComponentMountedRef.current = false;
      
      // Cleanup only the model and PIXI app
      if (modelRef.current) {
        modelRef.current.destroy();
        modelRef.current = null;
      }
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
      }
      
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
      cancelAnimationFrame(animationId);
    };
  }, [live2dPath]);

  // Create hidden video element for camera input (only once)
  useEffect(() => {
    // Only create video element if it doesn't exist
    if (!globalVideoElement) {
      const video = document.createElement('video');
      video.style.position = 'absolute';
      video.style.top = '-9999px';
      video.style.left = '-9999px';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0';
      video.style.pointerEvents = 'none';
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      
      document.body.appendChild(video);
      globalVideoElement = video;

      // Get camera stream
      navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: 640, 
          height: 360,
          facingMode: 'user'
        } 
      }).then(stream => {
        video.srcObject = stream;
      }).catch(error => {
        console.warn('[Live2DPreview] Camera access failed:', error);
      });
    }

    // Cleanup function - only cleanup when component is unmounted AND no other instances exist
    return () => {
      // We'll handle global cleanup in a separate effect or when the app closes
    };
  }, []);

  // Global cleanup effect - runs when component unmounts
  useEffect(() => {
    return () => {
      // Only cleanup global instances if this is the last component
      // For now, we'll keep them alive to prevent the deletion errors
      // They will be cleaned up when the page is refreshed or closed
    };
  }, []);

  if (error) {
    return (
      <div ref={containerRef} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
        <div>😔 Failed to load Live2D model: {error}</div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          zIndex: 10
        }}>
          {isMediaPipeInitializing ? 'Setting up camera...' : 'Loading model...'}
        </div>
      )}
      
      {!faceDetected && !isLoading && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          background: 'rgba(255, 0, 0, 0.8)',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          zIndex: 10
        }}>
          Face not detected
        </div>
      )}
      
      {faceDetected && !isLoading && showInstruction && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          background: 'rgba(0, 0, 0, 0.7)',
          padding: '12px 20px',
          borderRadius: '20px',
          fontSize: '14px',
          zIndex: 10,
          textAlign: 'center',
          maxWidth: '280px',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ marginBottom: '4px', fontSize: '16px' }}>Try moving your head, blinking, or talking!</div>
          <div style={{ fontSize: '12px', opacity: 0.8 }}>Your avatar will follow your movements</div>
        </div>
      )}
    </div>
  );
});

export default Live2DPreview; 